import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@/api/admin'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Plus, X } from 'lucide-react'
import { AddressAutocomplete } from '@/components/shared/AddressAutocomplete'
import { addressLabels } from '@/lib/address'
import { isUs } from '@/lib/dates'
import { useAdminDates } from '@/hooks/useDates'

const selectCls = "h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"

type QuickForm = {
  name: string
  addressLine1: string
  city: string
  postcode: string
  notes: string
}

const emptyQuick = (): QuickForm => ({ name: '', addressLine1: '', city: '', postcode: '', notes: '' })

export function VenueSelector({
  value,
  onChange,
  label = 'Venue',
  id = 'venue-selector',
  clientId,
  disabled = false,
}: {
  value: string | null
  onChange: (id: string | null) => void
  label?: string
  id?: string
  clientId?: string | null
  disabled?: boolean
}) {
  const { region } = useAdminDates()
  const labels = addressLabels(region)
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [quick, setQuick] = useState<QuickForm>(emptyQuick())

  const { data: venues = [] } = useQuery({
    queryKey: ['venues'],
    queryFn: () => adminApi.getVenues().then(r => r.data),
  })

  // Show client venues first, then global ones; exclude venues from other clients
  const filtered = venues.filter(v => !v.clientId || v.clientId === clientId)
  const clientVenues = filtered.filter(v => v.clientId)
  const globalVenues = filtered.filter(v => !v.clientId)

  const createMutation = useMutation({
    mutationFn: () => adminApi.createVenue({
      name: quick.name.trim(),
      addressLine1: quick.addressLine1.trim() || null,
      addressLine2: null,
      city: quick.city.trim() || null,
      county: null,
      postcode: quick.postcode.trim() || null,
      notes: quick.notes.trim() || null,
      clientId: clientId ?? null,
    }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['venues'] })
      onChange(res.data.id)
      setAdding(false)
      setQuick(emptyQuick())
    },
  })

  function venueLabel(v: typeof venues[0]) {
    const parts = [v.city, isUs(region) ? v.county : null, v.postcode].filter(Boolean).join(' ')
    return parts ? `${v.name} — ${parts}` : v.name
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>

      {adding ? (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2">
          <p className="text-xs font-medium text-indigo-700">New venue</p>
          <Input
            placeholder="Name *"
            value={quick.name}
            onChange={e => setQuick(q => ({ ...q, name: e.target.value }))}
            autoFocus
          />
          <AddressAutocomplete
            region={region}
            onSelect={r => setQuick(q => ({ ...q, addressLine1: r.addressLine1, city: r.city, postcode: r.postcode }))}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="Street address"
              value={quick.addressLine1}
              onChange={e => setQuick(q => ({ ...q, addressLine1: e.target.value }))}
            />
            <Input
              placeholder="Town / city"
              value={quick.city}
              onChange={e => setQuick(q => ({ ...q, city: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder={labels.postcode}
              value={quick.postcode}
              onChange={e => setQuick(q => ({ ...q, postcode: e.target.value }))}
            />
            <Input
              placeholder="Notes (optional)"
              value={quick.notes}
              onChange={e => setQuick(q => ({ ...q, notes: e.target.value }))}
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              size="sm"
              onClick={() => createMutation.mutate()}
              disabled={!quick.name.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? 'Saving…' : 'Save venue'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => { setAdding(false); setQuick(emptyQuick()) }}
            >
              <X size={13} />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <select
            id={id}
            value={value ?? ''}
            onChange={e => onChange(e.target.value || null)}
            disabled={disabled}
            className={selectCls + ' flex-1 disabled:cursor-not-allowed disabled:opacity-50'}
          >
            <option value="">No venue</option>
            {clientVenues.length > 0 && globalVenues.length > 0 ? (
              <>
                <optgroup label="This client">
                  {clientVenues.map(v => <option key={v.id} value={v.id}>{venueLabel(v)}</option>)}
                </optgroup>
                <optgroup label="Global">
                  {globalVenues.map(v => <option key={v.id} value={v.id}>{venueLabel(v)}</option>)}
                </optgroup>
              </>
            ) : (
              filtered.map(v => <option key={v.id} value={v.id}>{venueLabel(v)}</option>)
            )}
          </select>
          {!disabled && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              title="Add new venue"
              className="shrink-0 inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors cursor-pointer"
            >
              <Plus size={13} /> New
            </button>
          )}
        </div>
      )}
    </div>
  )
}
